<template>
  <div class="analysis-page">
    <!-- 顶部：Tab + 通用筛选 -->
    <el-card class="filter-card" shadow="never">
      <div class="fg-row">
        <div class="fg-item">
          <el-radio-group v-model="activeTab" size="small" @change="onTabChange">
            <el-radio-button label="dup">重复推分析</el-radio-button>
            <el-radio-button label="channel">渠道分布</el-radio-button>
            <el-radio-button label="order">订单分析</el-radio-button>
            <el-radio-button label="eff">员工效能</el-radio-button>
          </el-radio-group>
        </div>
        <div class="fg-item">
          <span class="fg-label">统计区间</span>
          <el-date-picker
            v-model="range"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :picker-options="pickerOptions"
            style="width: 280px"
          />
        </div>
        <div v-if="isCompany" class="fg-item">
          <span class="fg-label">战队</span>
          <el-select v-model="filterUnitId" placeholder="全部战队" clearable size="small" style="width: 140px" @change="onUnitChange">
            <el-option v-for="u in units" :key="u.id" :label="u.name" :value="u.id" />
          </el-select>
        </div>
        <div v-if="isCompany" class="fg-item">
          <span class="fg-label">小组</span>
          <el-select v-model="filterTeamId" placeholder="全部小组" clearable size="small" style="width: 140px" @change="onTeamChange">
            <el-option v-for="t in teams" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
        </div>
        <div v-if="isCompany" class="fg-item">
          <span class="fg-label">员工</span>
          <el-select v-model="filterStaffId" placeholder="全部员工" clearable size="small" style="width: 140px" @change="reload">
            <el-option v-for="s in staffOptions" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </div>
        <el-button type="primary" icon="el-icon-search" size="small" @click="reload">查询</el-button>
      </div>
    </el-card>

    <!-- ========== Tab1 重复推分析 ========== -->
    <template v-if="activeTab === 'dup'">
      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">重复推总览</span>
          <span class="block-sub">同一量账号被 ≥2 位运营录入即判定重复推 · 仅统计展示</span>
        </div>
        <div class="kpi-row">
          <div class="kpi kpi-red">
            <span class="kpi-label">重复线索数</span>
            <span class="kpi-val">{{ dupSummary.dup_count || 0 }}<small>条</small></span>
            <span class="kpi-sub">占区间录入 {{ dupSummary.dup_rate || 0 }}%</span>
          </div>
          <div class="kpi kpi-blue">
            <span class="kpi-label">涉及量账号</span>
            <span class="kpi-val">{{ dupSummary.account_count || 0 }}<small>个</small></span>
            <span class="kpi-sub">多产生 {{ dupSummary.extra_follow || 0 }} 次重复跟进</span>
          </div>
          <div class="kpi kpi-orange">
            <span class="kpi-label">重复率</span>
            <span class="kpi-val">{{ dupSummary.dup_rate || 0 }}<small>%</small></span>
            <span class="kpi-sub">重复率 = 重复线索 ÷ 录入总量</span>
          </div>
          <div class="kpi kpi-green">
            <span class="kpi-label">涉及运营</span>
            <span class="kpi-val">{{ dupSummary.staff_count || 0 }}<small>人</small></span>
            <span class="kpi-sub">人均重复 {{ dupAvg }} 条</span>
          </div>
        </div>
        <div class="chart-duo">
          <div class="chart-card">
            <div class="chart-head"><span>重复率趋势</span><small>{{ rangeText }} 每日</small></div>
            <div ref="dupTrendChart" class="chart-box sm"></div>
          </div>
          <div class="chart-card">
            <div class="chart-head"><span>运营重复推排行</span><small>Top 8 · 重复录入条数</small></div>
            <div ref="dupStaffChart" class="chart-box sm"></div>
          </div>
        </div>
      </el-card>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">重复线索明细</span>
          <span class="block-sub">共 {{ dupTotal }} 个量账号 · 默认按重复次数降序 · 仅查看不处理</span>
        </div>
        <div class="toolbar">
          <div class="toolbar-left">
            <el-input
              v-model="dupKeyword"
              size="small"
              placeholder="搜索量账号"
              clearable
              style="width: 220px"
              @keyup.enter.native="onDupSearch"
              @clear="onDupSearch"
            />
            <el-button size="small" icon="el-icon-search" @click="onDupSearch">搜索</el-button>
          </div>
          <span class="toolbar-tip">点击「详情」查看该量账号的全部重复线索</span>
        </div>
        <el-table :data="dupList" :header-cell-style="headerStyle" empty-text="区间内暂无重复推" size="small">
          <el-table-column label="量账号" min-width="150">
            <template slot-scope="{ row }"><span class="acc">{{ row.liang_account }}</span></template>
          </el-table-column>
          <el-table-column label="重复次数" width="100" align="center">
            <template slot-scope="{ row }"><span class="tag" :class="dupCountCls(row.dup_count)">{{ row.dup_count }} 次</span></template>
          </el-table-column>
          <el-table-column prop="first_time" label="首次录入" width="110" />
          <el-table-column prop="last_time" label="最近录入" width="110" />
          <el-table-column label="涉及运营" min-width="200">
            <template slot-scope="{ row }">
              <span v-for="n in row.staff_names" :key="n" class="tag tag-blue chip">{{ n }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="channel_name" label="渠道" min-width="100" />
          <el-table-column prop="assign_name" label="当前归属" min-width="100" />
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }"><span class="tag" :class="'tag-' + row.status_cls">{{ row.status_text }}</span></template>
          </el-table-column>
          <el-table-column label="操作" width="80" align="center">
            <template slot-scope="{ row }"><span class="op" @click="showDupDetail(row)">详情</span></template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pager"
          background
          layout="total, prev, pager, next"
          :total="dupTotal"
          :page-size="dupLimit"
          :current-page.sync="dupPage"
          @current-change="dupLoad"
        />
      </el-card>
    </template>

    <!-- ========== Tab2 渠道分布 ========== -->
    <template v-if="activeTab === 'channel'">
      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">渠道来源总览</span>
          <span class="block-sub">有效线索 = 录入量 − 重复推 · 仅统计展示</span>
        </div>
        <div class="kpi-row">
          <div class="kpi kpi-blue">
            <span class="kpi-label">录入总量</span>
            <span class="kpi-val">{{ channelSummary.total || 0 }}<small>条</small></span>
            <span class="kpi-sub">{{ rangeText }}</span>
          </div>
          <div class="kpi kpi-green">
            <span class="kpi-label">有效线索</span>
            <span class="kpi-val">{{ channelSummary.valid || 0 }}<small>条</small></span>
            <span class="kpi-sub">已剔除重复推部分</span>
          </div>
          <div class="kpi kpi-orange">
            <span class="kpi-label">渠道重复推</span>
            <span class="kpi-val">{{ channelSummary.dup || 0 }}<small>条</small></span>
            <span class="kpi-sub">重复率 {{ channelDupRate }}%</span>
          </div>
          <div class="kpi kpi-red">
            <span class="kpi-label">TOP 渠道</span>
            <span class="kpi-val kpi-val-sm">{{ channelSummary.top_name || '—' }} {{ channelSummary.top_ratio || 0 }}<small>%</small></span>
            <span class="kpi-sub">按录入量占比</span>
          </div>
        </div>
        <div class="chart-duo">
          <div class="chart-card">
            <div class="chart-head"><span>来源占比</span><small>按录入量</small></div>
            <div ref="channelPieChart" class="chart-box sm"></div>
          </div>
          <div class="chart-card">
            <div class="chart-head"><span>各渠道录入 vs 重复</span><small>深色=有效 · 浅色=重复推</small></div>
            <div ref="channelStackChart" class="chart-box sm"></div>
          </div>
        </div>
      </el-card>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">渠道每日趋势</span>
          <span class="block-sub">Top 5 渠道 · {{ rangeText }}</span>
        </div>
        <div ref="channelTrendChart" class="chart-box"></div>
      </el-card>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">渠道明细</span>
          <span class="block-sub">重复率 = 重复推 ÷ 录入量 · 平均金额按该渠道成交订单均价</span>
        </div>
        <el-table :data="channelList" :header-cell-style="headerStyle" empty-text="区间内暂无录入" size="small">
          <el-table-column prop="name" label="渠道" min-width="120" />
          <el-table-column prop="total" label="录入量" width="90" align="center" />
          <el-table-column label="占比" width="90" align="center">
            <template slot-scope="{ row }">{{ row.ratio }}%</template>
          </el-table-column>
          <el-table-column prop="dup" label="重复推" width="90" align="center" />
          <el-table-column label="重复率" width="100" align="center">
            <template slot-scope="{ row }"><span class="tag" :class="dupRateCls(row.dup_rate)">{{ row.dup_rate }}%</span></template>
          </el-table-column>
          <el-table-column prop="valid" label="有效线索" width="100" align="center" />
          <el-table-column prop="dealt" label="成交" width="90" align="center" />
          <el-table-column label="成交率" width="100" align="center">
            <template slot-scope="{ row }"><span class="deal-rate">{{ row.deal_rate }}%</span></template>
          </el-table-column>
          <el-table-column label="平均金额" min-width="110" align="right">
            <template slot-scope="{ row }"><span class="money">¥ {{ fmtMoney(row.avg_amount) }}</span></template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>

    <!-- ========== Tab3 订单分析 ========== -->
    <template v-if="activeTab === 'order'">
      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">订单概览</span>
          <span class="block-sub">口径：区间内新单 + 历史未收清欠款单 · 账龄 = 创建至今 · 已收 = 已完成/已还款账单合计</span>
        </div>
        <div class="kpi-row six">
          <div class="kpi kpi-blue">
            <span class="kpi-label">订单总数</span>
            <span class="kpi-val">{{ orderSummary.order_count || 0 }}<small>单</small></span>
            <span class="kpi-sub">含历史未收清欠款单</span>
          </div>
          <div class="kpi kpi-green">
            <span class="kpi-label">订单总额</span>
            <span class="kpi-val">¥ {{ fmtMoney(orderSummary.amount) }}</span>
            <span class="kpi-sub">平均 ¥ {{ fmtMoney(orderSummary.avg_amount) }} / 单</span>
          </div>
          <div class="kpi kpi-orange">
            <span class="kpi-label">平均账龄</span>
            <span class="kpi-val">{{ orderSummary.avg_age || 0 }}<small>天</small></span>
            <span class="kpi-sub">账龄 ≥ 90 天预警 {{ orderSummary.warn_count || 0 }} 单</span>
          </div>
          <div class="kpi kpi-red">
            <span class="kpi-label">回款率</span>
            <span class="kpi-val">{{ orderSummary.recover_rate || 0 }}<small>%</small></span>
            <span class="kpi-sub">已收 ¥ {{ fmtMoney(orderSummary.paid) }} · 未收 ¥ {{ fmtMoney(orderSummary.unpaid) }}</span>
          </div>
          <div class="kpi kpi-blue">
            <span class="kpi-label">平均回款周期</span>
            <span class="kpi-val">{{ orderSummary.avg_settle_days || 0 }}<small>天</small></span>
            <span class="kpi-sub">已收清订单：下单 → 最后一笔收款</span>
          </div>
          <div class="kpi kpi-orange">
            <span class="kpi-label">逾期分期</span>
            <span class="kpi-val">{{ orderOverdueInst.count || 0 }}<small>期</small></span>
            <span class="kpi-sub">逾期金额 ¥ {{ fmtMoney(orderOverdueInst.amount) }}</span>
          </div>
        </div>
        <div class="recover-box">
          <div class="recover-head">
            <span class="recover-title">回款进度</span>
            <span class="mini">
              已收 <b>¥ {{ fmtMoney(orderSummary.paid) }}</b> ·
              未收 <b>¥ {{ fmtMoney(orderSummary.unpaid) }}</b> ·
              回款率 <b>{{ orderSummary.recover_rate || 0 }}%</b>
            </span>
          </div>
          <div class="pbar lg">
            <i class="paid" :style="{ width: orderRecoverRate + '%' }"></i>
            <i class="unpaid" :style="{ width: (100 - orderRecoverRate) + '%' }"></i>
          </div>
        </div>
      </el-card>

      <div class="chart-duo">
        <el-card class="block-card" shadow="never">
          <div slot="header" class="block-head">
            <span class="block-title">订单年龄分布</span>
            <span class="block-sub">创建至今账龄</span>
          </div>
          <div ref="orderAgeChart" class="chart-box sm"></div>
        </el-card>
        <el-card class="block-card" shadow="never">
          <div slot="header" class="block-head">
            <span class="block-title">订单金额分布</span>
            <span class="block-sub">按订单金额区间</span>
          </div>
          <div ref="orderAmountChart" class="chart-box sm"></div>
        </el-card>
      </div>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">账龄 × 回款</span>
          <span class="block-sub">各账龄段的已收清 / 未收清单量</span>
        </div>
        <div ref="orderRecoverChart" class="chart-box"></div>
      </el-card>

      <div class="chart-duo">
        <el-card class="block-card" shadow="never">
          <div slot="header" class="block-head">
            <span class="block-title">销售欠款排行</span>
            <span class="block-sub">Top 10 · 未收清订单欠款金额</span>
          </div>
          <div ref="orderDebtChart" class="chart-box sm"></div>
        </el-card>
        <el-card class="block-card" shadow="never">
          <div slot="header" class="block-head">
            <span class="block-title">逾期分期</span>
            <span class="block-sub">当前全部逾期 · 按逾期天数降序 Top 8</span>
          </div>
          <el-table :data="orderOverdueInst.list || []" :header-cell-style="headerStyle" empty-text="暂无逾期分期" size="mini">
            <el-table-column prop="order_sn" label="订单号" min-width="120" />
            <el-table-column prop="customer_name" label="客户" min-width="80" />
            <el-table-column prop="staff_name" label="销售" width="80" />
            <el-table-column label="期数" width="60" align="center">
              <template slot-scope="{ row }">第 {{ row.period_no }} 期</template>
            </el-table-column>
            <el-table-column label="金额" width="100" align="right">
              <template slot-scope="{ row }"><span class="money">¥ {{ fmtMoney(row.amount) }}</span></template>
            </el-table-column>
            <el-table-column prop="due_date" label="出账日" width="105" />
            <el-table-column label="逾期" width="90" align="center">
              <template slot-scope="{ row }"><span class="tag tag-red">{{ row.overdue_days }} 天</span></template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">订单明细</span>
          <span class="block-sub">共 {{ orderTotal }} 单 · 按账龄降序 · 账龄 ≥ 90 天且未收清红色预警</span>
        </div>
        <div class="toolbar">
          <span class="toolbar-tip">账龄 ≥ 90 天且未收清红色预警 · 仅查看不处理</span>
          <el-switch v-model="orderWarn" active-text="仅看预警" @change="onWarnChange" />
        </div>
        <el-table :data="orderList" :header-cell-style="headerStyle" empty-text="区间内暂无订单" size="small">
          <el-table-column prop="order_sn" label="订单号" min-width="130">
            <template slot-scope="{ row }"><span class="acc">{{ row.order_sn }}</span></template>
          </el-table-column>
          <el-table-column prop="customer_name" label="客户" min-width="90" />
          <el-table-column prop="product_name" label="商品" min-width="120" />
          <el-table-column label="金额" width="110" align="right">
            <template slot-scope="{ row }"><span class="money">¥ {{ fmtMoney(row.price) }}</span></template>
          </el-table-column>
          <el-table-column label="已收" width="110" align="right">
            <template slot-scope="{ row }"><span class="money" :class="{ none: !row.paid }">¥ {{ fmtMoney(row.paid) }}</span></template>
          </el-table-column>
          <el-table-column label="回款进度" width="120">
            <template slot-scope="{ row }">
              <div class="pbar" :title="'已收 ' + row.paid + ' / ' + row.price">
                <i class="paid" :style="{ width: paidPercent(row) + '%' }"></i>
                <i class="unpaid" :style="{ width: (100 - paidPercent(row)) + '%' }"></i>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="createtime" label="创建时间" width="110" />
          <el-table-column label="账龄" width="90" align="center">
            <template slot-scope="{ row }"><span class="tag" :class="ageCls(row.age_days)">{{ row.age_days }} 天</span></template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }"><span class="tag" :class="'tag-' + row.status_cls">{{ row.status_text }}</span></template>
          </el-table-column>
          <el-table-column prop="staff_name" label="归属销售" min-width="100" />
        </el-table>
        <el-pagination
          class="pager"
          background
          layout="total, prev, pager, next"
          :total="orderTotal"
          :page-size="orderLimit"
          :current-page.sync="orderPage"
          @current-change="orderLoad"
        />
      </el-card>
    </template>

    <!-- ========== Tab4 员工效能 ========== -->
    <template v-if="activeTab === 'eff'">
      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">效能漏斗</span>
          <span class="block-sub">录入 → 加微 → 成交 · 按录入人统计（运营侧）</span>
        </div>
        <div class="kpi-row">
          <div class="kpi kpi-blue">
            <span class="kpi-label">录入总量</span>
            <span class="kpi-val">{{ effFunnel.total || 0 }}<small>条</small></span>
            <span class="kpi-sub">{{ rangeText }}</span>
          </div>
          <div class="kpi kpi-green">
            <span class="kpi-label">加微率</span>
            <span class="kpi-val">{{ effFunnel.added_rate || 0 }}<small>%</small></span>
            <span class="kpi-sub">已加微 {{ effFunnel.added || 0 }} 条</span>
          </div>
          <div class="kpi kpi-orange">
            <span class="kpi-label">成交率</span>
            <span class="kpi-val">{{ effFunnel.deal_rate || 0 }}<small>%</small></span>
            <span class="kpi-sub">已成交 {{ effFunnel.dealt || 0 }} 条</span>
          </div>
          <div class="kpi kpi-red">
            <span class="kpi-label">人均订单额</span>
            <span class="kpi-val">¥ {{ fmtMoney(effSalesSummary.avg_amount) }}</span>
            <span class="kpi-sub">有产出销售 {{ effSalesSummary.staff_count || 0 }} 人 · 订单 {{ effSalesSummary.order_count || 0 }} 单</span>
          </div>
        </div>
        <div class="chart-card">
          <div class="chart-head"><span>转化漏斗</span><small>录入 {{ effFunnel.total || 0 }} → 加微 {{ effFunnel.added || 0 }} → 成交 {{ effFunnel.dealt || 0 }}</small></div>
          <div ref="effFunnelChart" class="chart-box sm"></div>
        </div>
      </el-card>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">运营效能排行</span>
          <span class="block-sub">按录入人（推广侧）· 成交优先 · Top 50</span>
        </div>
        <el-table :data="effOperatorList" :header-cell-style="headerStyle" empty-text="区间内暂无录入" size="small">
          <el-table-column label="排名" width="70" align="center">
            <template slot-scope="{ $index }"><span class="rank">{{ $index + 1 }}</span></template>
          </el-table-column>
          <el-table-column prop="name" label="录入人" min-width="110" />
          <el-table-column prop="unit_name" label="战队" min-width="110" />
          <el-table-column prop="team_name" label="小组" min-width="110" />
          <el-table-column prop="total" label="录入量" width="90" align="center" />
          <el-table-column prop="added" label="已加微" width="90" align="center" />
          <el-table-column label="加微率" width="100" align="center">
            <template slot-scope="{ row }"><span class="tag" :class="rateCls(row.added_rate, 40, 20)">{{ row.added_rate }}%</span></template>
          </el-table-column>
          <el-table-column prop="dealt" label="成交" width="90" align="center" />
          <el-table-column label="成交率" width="100" align="center">
            <template slot-scope="{ row }"><span class="deal-rate">{{ row.deal_rate }}%</span></template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card class="block-card" shadow="never">
        <div slot="header" class="block-head">
          <span class="block-title">销售产出排行</span>
          <span class="block-sub">按归属销售 · 订单金额降序 · Top 50（已收 = 已完成/已还款账单）</span>
        </div>
        <el-table :data="effSalesList" :header-cell-style="headerStyle" empty-text="区间内暂无线索与订单" size="small">
          <el-table-column label="排名" width="70" align="center">
            <template slot-scope="{ $index }"><span class="rank">{{ $index + 1 }}</span></template>
          </el-table-column>
          <el-table-column prop="name" label="销售" min-width="100" />
          <el-table-column prop="unit_name" label="战队" min-width="100" />
          <el-table-column prop="team_name" label="小组" min-width="100" />
          <el-table-column prop="leads" label="线索" width="80" align="center" />
          <el-table-column prop="dealt" label="成交线索" width="90" align="center" />
          <el-table-column prop="order_count" label="订单数" width="90" align="center" />
          <el-table-column label="订单金额" width="120" align="right">
            <template slot-scope="{ row }"><span class="money">¥ {{ fmtMoney(row.amount) }}</span></template>
          </el-table-column>
          <el-table-column label="已收" width="120" align="right">
            <template slot-scope="{ row }"><span class="money" :class="{ none: !row.paid }">¥ {{ fmtMoney(row.paid) }}</span></template>
          </el-table-column>
          <el-table-column label="未收" width="120" align="right">
            <template slot-scope="{ row }"><span class="money" :class="{ none: row.unpaid > 0 }">¥ {{ fmtMoney(row.unpaid) }}</span></template>
          </el-table-column>
          <el-table-column label="回款率" width="100" align="center">
            <template slot-scope="{ row }"><span class="tag" :class="rateCls(row.recover_rate, 90, 60)">{{ row.recover_rate }}%</span></template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>

    <!-- 重复推详情 -->
    <el-dialog title="重复推详情" :visible.sync="dupDetailVisible" width="620px" append-to-body>
      <div v-if="dupDetailRow" class="detail-wrap">
        <div class="detail-head">
          <span class="detail-acc">{{ dupDetailRow.liang_account }}</span>
          <span class="detail-sub">
            重复 {{ dupDetailRow.dup_count }} 次 · 涉及 {{ (dupDetailRow.staff_names || []).length }} 位运营 ·
            首次 {{ dupDetailRow.first_time }} · 最近 {{ dupDetailRow.last_time }}
          </span>
        </div>
        <el-table :data="dupDetailRow.details || []" :header-cell-style="headerStyle" empty-text="暂无明细" size="mini">
          <el-table-column prop="staff_name" label="录入人" width="120" />
          <el-table-column prop="createtime" label="录入时间" width="140" />
          <el-table-column prop="channel_name" label="渠道" min-width="110" />
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }"><span class="tag" :class="'tag-' + row.status_cls">{{ row.status_text }}</span></template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { statsDuplicate, statsChannel, statsOrder, statsEfficiency } from '@/api/stats'
import { unitList, teamList, staffList } from '@/api/org'
import * as echarts from 'echarts'

// 图表通用
const PALETTE = ['#3668FC', '#14B8A6', '#8B5CF6', '#F59E0B', '#E5484D', '#16A34A', '#94A3B8']
const AXIS_LINE = { lineStyle: { color: '#E5E9F2' } }
const AXIS_LABEL = { color: '#8A94AD' }
const SPLIT_LINE = { lineStyle: { color: '#F0F3F9' } }
const GRID = { left: 12, right: 16, top: 24, bottom: 10, containLabel: true }
const AGE_LABELS = ['≤7天', '8-30天', '31-90天', '91天+']
const AMOUNT_LABELS = ['<1千', '1千-3千', '3千-5千', '5千-1万', '>1万']

export default {
  name: 'StatsAnalysis',
  data() {
    return {
      user: this.$store.state.user || {},
      activeTab: 'dup',
      range: null,
      pickerOptions: {
        disabledDate: d => d.getTime() > Date.now()
      },
      // 组织筛选（仅机构管理员可选）
      filterUnitId: null,
      filterTeamId: null,
      filterStaffId: null,
      units: [],
      teams: [],
      staffOptions: [],
      // 重复推
      dupKeyword: '',
      dupSummary: {},
      dupTrend: [],
      dupStaffTop: [],
      dupList: [],
      dupTotal: 0,
      dupPage: 1,
      dupLimit: 20,
      dupDetailVisible: false,
      dupDetailRow: null,
      // 渠道
      channelSummary: {},
      channelList: [],
      channelDays: [],
      channelSeries: [],
      channelStack: { names: [], valid: [], dup: [] },
      // 订单
      orderSummary: {},
      orderAgeDist: [],
      orderAmountDist: [],
      orderRecoverDist: [],
      orderStaffDebt: [],
      orderOverdueInst: {},
      orderList: [],
      orderTotal: 0,
      orderPage: 1,
      orderLimit: 20,
      orderWarn: false,
      // 员工效能
      effFunnel: {},
      effOperatorList: [],
      effSalesList: [],
      effSalesSummary: {},
      // 图表实例
      charts: {},
      headerStyle: { background: '#F7F9FC', color: '#18233C', fontWeight: 600 }
    }
  },
  computed: {
    isCompany() {
      return this.user.type === 'company'
    },
    rangeText() {
      if (!this.range || this.range.length !== 2) return '全部'
      return this.range[0] + ' ~ ' + this.range[1]
    },
    dupAvg() {
      const s = this.dupSummary
      return s.staff_count > 0 ? (s.dup_count / s.staff_count).toFixed(1) : '0'
    },
    channelDupRate() {
      const s = this.channelSummary
      return s.total > 0 ? (s.dup / s.total * 100).toFixed(1) : '0'
    },
    orderRecoverRate() {
      return Math.min(100, Math.max(0, parseFloat(this.orderSummary.recover_rate || 0)))
    }
  },
  created() {
    // 默认区间：最近一个月（含今天共 30 天）
    const now = new Date()
    const p = n => (n < 10 ? '0' + n : '' + n)
    const start = new Date(now.getTime() - 29 * 86400000)
    this.range = [
      start.getFullYear() + '-' + p(start.getMonth() + 1) + '-' + p(start.getDate()),
      now.getFullYear() + '-' + p(now.getMonth() + 1) + '-' + p(now.getDate())
    ]
    if (this.isCompany) this.loadUnits()
    this.reload()
  },
  mounted() {
    window.addEventListener('resize', this.resizeCharts)
    this.$nextTick(() => this.drawCharts())
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
  },
  methods: {
    onTabChange() {
      this.reload()
    },
    /* ---- 组织选项（与统计分析页一致） ---- */
    loadUnits() {
      unitList()
        .then(d => { this.units = d.list || [] })
        .catch(() => { this.units = [] })
    },
    onUnitChange() {
      this.filterTeamId = null
      this.filterStaffId = null
      if (!this.filterUnitId) {
        this.teams = []
        this.staffOptions = []
        this.reload()
        return
      }
      teamList({ unit_id: this.filterUnitId })
        .then(dt => {
          this.teams = dt.list || []
          return staffList({ unit_id: this.filterUnitId })
        })
        .then(ds => { this.staffOptions = ds.list || [] })
        .catch(() => {})
        .finally(() => this.reload())
    },
    onTeamChange() {
      this.filterStaffId = null
      if (!this.filterTeamId) {
        this.staffOptions = []
        this.reload()
        return
      }
      staffList({ team_id: this.filterTeamId })
        .then(ds => { this.staffOptions = ds.list || [] })
        .catch(() => {})
        .finally(() => this.reload())
    },
    /* ---- 加载 ---- */
    buildFilters() {
      const p = {}
      if (this.range && this.range.length === 2) { p.start_date = this.range[0]; p.end_date = this.range[1] }
      if (this.isCompany) {
        if (this.filterUnitId) p.unit_id = this.filterUnitId
        if (this.filterTeamId) p.team_id = this.filterTeamId
        if (this.filterStaffId) p.staff_id = this.filterStaffId
      }
      return p
    },
    reload() {
      this.dupPage = 1
      this.orderPage = 1
      this.disposeCharts()
      this[this.activeTab + 'Load']()
    },
    dupLoad() {
      const params = this.buildFilters()
      params.keyword = this.dupKeyword
      params.page = this.dupPage
      params.limit = this.dupLimit
      statsDuplicate(params)
        .then(d => {
          this.dupSummary = (d && d.summary) || {}
          this.dupTrend = (d && d.trend) || []
          this.dupStaffTop = (d && d.staff_top) || []
          this.dupList = (d && d.list) || []
          this.dupTotal = (d && d.total) || 0
          this.$nextTick(() => { if (this.activeTab === 'dup') this.drawDupCharts() })
        })
        .catch(() => {
          this.dupSummary = {}; this.dupTrend = []; this.dupStaffTop = []; this.dupList = []; this.dupTotal = 0
        })
    },
    onDupSearch() {
      this.dupPage = 1
      this.dupLoad()
    },
    channelLoad() {
      statsChannel(this.buildFilters())
        .then(d => {
          this.channelSummary = (d && d.summary) || {}
          this.channelList = (d && d.list) || []
          this.channelDays = (d && d.days) || []
          this.channelSeries = (d && d.series) || []
          this.channelStack = (d && d.stack) || { names: [], valid: [], dup: [] }
          this.$nextTick(() => { if (this.activeTab === 'channel') this.drawChannelCharts() })
        })
        .catch(() => {
          this.channelSummary = {}; this.channelList = []; this.channelDays = []; this.channelSeries = []
          this.channelStack = { names: [], valid: [], dup: [] }
        })
    },
    orderLoad() {
      const params = this.buildFilters()
      params.page = this.orderPage
      params.limit = this.orderLimit
      if (this.orderWarn) params.warn = 1
      statsOrder(params)
        .then(d => {
          this.orderSummary = (d && d.summary) || {}
          this.orderAgeDist = (d && d.age_dist) || []
          this.orderAmountDist = (d && d.amount_dist) || []
          this.orderRecoverDist = (d && d.recover_dist) || []
          this.orderStaffDebt = (d && d.staff_debt) || []
          this.orderOverdueInst = (d && d.overdue_inst) || {}
          this.orderList = (d && d.list) || []
          this.orderTotal = (d && d.total) || 0
          this.$nextTick(() => { if (this.activeTab === 'order') this.drawOrderCharts() })
        })
        .catch(() => {
          this.orderSummary = {}; this.orderAgeDist = []; this.orderAmountDist = []; this.orderRecoverDist = []
          this.orderStaffDebt = []; this.orderOverdueInst = {}
          this.orderList = []; this.orderTotal = 0
        })
    },
    effLoad() {
      statsEfficiency(this.buildFilters())
        .then(d => {
          this.effFunnel = (d && d.funnel) || {}
          this.effOperatorList = (d && d.operator_list) || []
          this.effSalesList = (d && d.sales_list) || []
          this.effSalesSummary = (d && d.sales_summary) || {}
          this.$nextTick(() => { if (this.activeTab === 'eff') this.drawEffCharts() })
        })
        .catch(() => {
          this.effFunnel = {}; this.effOperatorList = []; this.effSalesList = []; this.effSalesSummary = {}
        })
    },
    onWarnChange() {
      this.orderPage = 1
      this.orderLoad()
    },
    showDupDetail(row) {
      this.dupDetailRow = row
      this.dupDetailVisible = true
    },
    /* ---- 图表实例管理 ---- */
    initChart(key, refName, option) {
      const el = this.$refs[refName]
      if (!el) return
      if (this.charts[key]) { this.charts[key].dispose() }
      const c = echarts.init(el)
      c.setOption(option)
      this.charts[key] = c
    },
    disposeCharts() {
      Object.keys(this.charts).forEach(k => { if (this.charts[k]) this.charts[k].dispose() })
      this.charts = {}
    },
    resizeCharts() {
      Object.keys(this.charts).forEach(k => { if (this.charts[k]) this.charts[k].resize() })
    },
    drawCharts() {
      if (this.activeTab === 'dup') this.drawDupCharts()
      else if (this.activeTab === 'channel') this.drawChannelCharts()
      else if (this.activeTab === 'order') this.drawOrderCharts()
      else if (this.activeTab === 'eff') this.drawEffCharts()
    },
    /* ---- 重复推图表 ---- */
    drawDupCharts() {
      this.drawDupTrend()
      this.drawDupStaff()
    },
    drawDupTrend() {
      const trend = this.dupTrend || []
      if (!trend.length) return
      this.initChart('dupTrend', 'dupTrendChart', {
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const d = trend[params[0].dataIndex] || {}
            return d.day + '<br/>录入 ' + d.total + ' 条 · 重复 ' + d.dup + ' 条<br/>重复率 <b>' + d.rate + '%</b>'
          }
        },
        grid: GRID,
        xAxis: { type: 'category', boundaryGap: false, data: trend.map(r => r.day), axisLine: AXIS_LINE, axisLabel: Object.assign({ fontSize: 10 }, AXIS_LABEL) },
        yAxis: { type: 'value', axisLabel: Object.assign({ formatter: '{value}%' }, AXIS_LABEL), splitLine: SPLIT_LINE },
        series: [{
          name: '重复率', type: 'line', smooth: true, symbol: 'none',
          data: trend.map(r => r.rate),
          lineStyle: { width: 2, color: '#E5484D' }, itemStyle: { color: '#E5484D' },
          areaStyle: { color: 'rgba(229,72,77,0.10)' }
        }]
      })
    },
    drawDupStaff() {
      const top = (this.dupStaffTop || []).slice().reverse()
      if (!top.length) return
      this.initChart('dupStaff', 'dupStaffChart', {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: p => p[0].name + '：' + p[0].value + ' 条' },
        grid: { left: 12, right: 36, top: 10, bottom: 10, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, splitLine: SPLIT_LINE, axisLabel: AXIS_LABEL },
        yAxis: { type: 'category', data: top.map(r => r.name), axisLine: AXIS_LINE, axisLabel: { color: '#18233C' } },
        series: [{
          type: 'bar', barWidth: 12,
          data: top.map(r => r.count),
          itemStyle: {
            borderRadius: [0, 6, 6, 0],
            color: p => (top.length - 1 - p.dataIndex) < 3 ? '#E5484D' : '#3668FC'
          },
          label: { show: true, position: 'right', color: '#8A94AD', fontSize: 11 }
        }]
      })
    },
    /* ---- 渠道图表 ---- */
    drawChannelCharts() {
      this.drawChannelPie()
      this.drawChannelStack()
      this.drawChannelTrend()
    },
    drawChannelPie() {
      const list = this.channelList || []
      if (!list.length) return
      this.initChart('channelPie', 'channelPieChart', {
        color: PALETTE,
        tooltip: { trigger: 'item', formatter: '{b}：{c} 条（{d}%）' },
        legend: { bottom: 0, icon: 'circle', itemWidth: 10, itemHeight: 10, textStyle: { color: '#8A94AD', fontSize: 12 } },
        series: [{
          type: 'pie', radius: ['45%', '68%'], center: ['50%', '44%'],
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
          label: { color: '#18233C', fontSize: 12 },
          data: list.map(c => ({ name: c.name, value: c.total }))
        }]
      })
    },
    drawChannelStack() {
      const st = this.channelStack || {}
      if (!st.names || !st.names.length) return
      this.initChart('channelStack', 'channelStackChart', {
        color: ['#3668FC', '#F3B94C'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { top: 0, icon: 'circle', itemWidth: 10, itemHeight: 10, textStyle: { color: '#8A94AD', fontSize: 12 }, data: ['有效', '重复推'] },
        grid: Object.assign({}, GRID, { top: 30 }),
        xAxis: { type: 'category', data: st.names, axisLine: AXIS_LINE, axisLabel: Object.assign({ interval: 0, rotate: st.names.length > 6 ? 24 : 0 }, AXIS_LABEL) },
        yAxis: { type: 'value', minInterval: 1, splitLine: SPLIT_LINE, axisLabel: AXIS_LABEL },
        series: [
          { name: '有效', type: 'bar', stack: 't', barWidth: '40%', data: st.valid },
          { name: '重复推', type: 'bar', stack: 't', itemStyle: { borderRadius: [6, 6, 0, 0] }, data: st.dup }
        ]
      })
    },
    drawChannelTrend() {
      const days = this.channelDays || []
      const series = (this.channelSeries || []).map((s, i) => ({
        name: s.name, type: 'line', smooth: true, symbol: 'none', data: s.data,
        lineStyle: { width: 2, color: PALETTE[i % PALETTE.length] },
        itemStyle: { color: PALETTE[i % PALETTE.length] }
      }))
      if (!days.length || !series.length) return
      this.initChart('channelTrend', 'channelTrendChart', {
        color: PALETTE,
        tooltip: { trigger: 'axis' },
        legend: { top: 0, icon: 'circle', itemWidth: 10, itemHeight: 10, textStyle: { color: '#8A94AD', fontSize: 12 } },
        grid: Object.assign({}, GRID, { top: 34 }),
        xAxis: { type: 'category', boundaryGap: false, data: days, axisLine: AXIS_LINE, axisLabel: Object.assign({ fontSize: 10 }, AXIS_LABEL) },
        yAxis: { type: 'value', minInterval: 1, splitLine: SPLIT_LINE, axisLabel: AXIS_LABEL },
        series
      })
    },
    /* ---- 订单图表 ---- */
    drawOrderCharts() {
      this.drawOrderAge()
      this.drawOrderAmount()
      this.drawOrderRecover()
      this.drawOrderDebt()
    },
    /* 销售欠款排行（横向条形） */
    drawOrderDebt() {
      const debt = (this.orderStaffDebt || []).slice().reverse()
      if (!debt.length) return
      this.initChart('orderDebt', 'orderDebtChart', {
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' },
          formatter: p => {
            const d = debt[p[0].dataIndex] || {}
            return d.name + '<br/>欠款 ¥ ' + this.fmtMoney(d.unpaid) + '<br/>涉及 ' + d.order_count + ' 单 · 最长账龄 ' + d.max_age + ' 天'
          }
        },
        grid: { left: 12, right: 46, top: 10, bottom: 10, containLabel: true },
        xAxis: { type: 'value', splitLine: SPLIT_LINE, axisLabel: Object.assign({ formatter: v => (v >= 10000 ? (v / 10000) + '万' : v) }, AXIS_LABEL) },
        yAxis: { type: 'category', data: debt.map(r => r.name), axisLine: AXIS_LINE, axisLabel: { color: '#18233C' } },
        series: [{
          type: 'bar', barWidth: 12,
          data: debt.map(r => r.unpaid),
          itemStyle: {
            borderRadius: [0, 6, 6, 0],
            color: p => (debt.length - 1 - p.dataIndex) < 3 ? '#E5484D' : '#3668FC'
          },
          label: {
            show: true, position: 'right', color: '#8A94AD', fontSize: 11,
            formatter: p => this.fmtMoney(p.value)
          }
        }]
      })
    },
    drawOrderAge() {
      const dist = this.orderAgeDist || []
      if (!dist.length) return
      this.initChart('orderAge', 'orderAgeChart', {
        tooltip: { trigger: 'axis' },
        grid: GRID,
        xAxis: { type: 'category', data: AGE_LABELS, axisLine: AXIS_LINE, axisLabel: AXIS_LABEL },
        yAxis: { type: 'value', minInterval: 1, splitLine: SPLIT_LINE, axisLabel: AXIS_LABEL },
        series: [{
          type: 'bar', barWidth: '42%', data: dist,
          itemStyle: { borderRadius: [6, 6, 0, 0], color: p => p.dataIndex === 3 ? '#E5484D' : '#3668FC' },
          label: { show: true, position: 'top', color: '#8A94AD', fontSize: 11 }
        }]
      })
    },
    drawOrderAmount() {
      const dist = this.orderAmountDist || []
      if (!dist.length) return
      this.initChart('orderAmount', 'orderAmountChart', {
        tooltip: { trigger: 'axis' },
        grid: GRID,
        xAxis: { type: 'category', data: AMOUNT_LABELS, axisLine: AXIS_LINE, axisLabel: AXIS_LABEL },
        yAxis: { type: 'value', minInterval: 1, splitLine: SPLIT_LINE, axisLabel: AXIS_LABEL },
        series: [{
          type: 'bar', barWidth: '42%', data: dist,
          itemStyle: { borderRadius: [6, 6, 0, 0], color: '#14B8A6' },
          label: { show: true, position: 'top', color: '#8A94AD', fontSize: 11 }
        }]
      })
    },
    drawOrderRecover() {
      const dist = this.orderRecoverDist || []
      if (!dist.length) return
      this.initChart('orderRecover', 'orderRecoverChart', {
        color: ['#16A34A', '#F3B94C'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { top: 0, icon: 'circle', itemWidth: 10, itemHeight: 10, textStyle: { color: '#8A94AD', fontSize: 12 }, data: ['已收清', '未收清'] },
        grid: Object.assign({}, GRID, { top: 30 }),
        xAxis: { type: 'category', data: AGE_LABELS, axisLine: AXIS_LINE, axisLabel: AXIS_LABEL },
        yAxis: { type: 'value', minInterval: 1, splitLine: SPLIT_LINE, axisLabel: AXIS_LABEL },
        series: [
          { name: '已收清', type: 'bar', stack: 'r', barWidth: '42%', data: dist.map(v => v.paid) },
          { name: '未收清', type: 'bar', stack: 'r', itemStyle: { borderRadius: [6, 6, 0, 0] }, data: dist.map(v => v.unpaid) }
        ]
      })
    },
    /* ---- 员工效能图表 ---- */
    drawEffCharts() {
      const f = this.effFunnel || {}
      if (!f.total && !f.added && !f.dealt) return
      this.initChart('effFunnel', 'effFunnelChart', {
        color: ['#3668FC', '#14B8A6', '#F59E0B'],
        tooltip: { trigger: 'item', formatter: '{b}：{c} 条' },
        series: [{
          type: 'funnel',
          left: '10%', right: '10%', top: 10, bottom: 10,
          minSize: '30%',
          label: { position: 'inside', color: '#fff', fontSize: 12, formatter: p => p.name + ' ' + p.value + ' 条' },
          itemStyle: { borderColor: '#fff', borderWidth: 2, borderRadius: 6 },
          data: [
            { name: '录入', value: f.total || 0 },
            { name: '加微', value: f.added || 0 },
            { name: '成交', value: f.dealt || 0 }
          ]
        }]
      })
    },
    /* ---- 展示工具 ---- */
    fmtMoney(n) {
      const v = parseFloat(n || 0)
      return v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    dupCountCls(c) {
      if (c >= 5) return 'tag-red'
      if (c >= 3) return 'tag-orange'
      return 'tag-gray'
    },
    dupRateCls(rate) {
      const r = parseFloat(rate || 0)
      if (r <= 0) return 'tag-green'
      if (r >= 8) return 'tag-red'
      if (r >= 5) return 'tag-orange'
      return 'tag-gray'
    },
    ageCls(days) {
      const d = parseInt(days || 0)
      if (d >= 90) return 'tag-red'
      if (d >= 31) return 'tag-orange'
      if (d >= 8) return 'tag-blue'
      return 'tag-gray'
    },
    /* 越高越好：≥good 绿 · ≥ok 橙 · 否则红 */
    rateCls(rate, good, ok) {
      const r = parseFloat(rate || 0)
      if (r >= good) return 'tag-green'
      if (r >= ok) return 'tag-orange'
      return 'tag-red'
    },
    paidPercent(row) {
      const price = parseFloat(row.price || 0)
      const paid = parseFloat(row.paid || 0)
      if (price <= 0) return 0
      return Math.min(100, Math.round(paid / price * 100))
    }
  }
}
</script>

<style scoped>
.analysis-page { padding: 0; }
.filter-card, .block-card { margin-bottom: 14px; border-radius: 10px; border: 1px solid #E5E9F2; }
.fg-row { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.fg-item { display: flex; align-items: center; gap: 6px; }
.fg-label { font-size: 13px; color: #5A6A85; white-space: nowrap; }
.block-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.block-title { font-size: 14px; font-weight: 700; color: #18233C; }
.block-sub { font-size: 12px; color: #8A94AD; }

/* KPI 卡（左侧色条） */
.kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
.kpi-row.six { grid-template-columns: repeat(3, 1fr); }
.kpi { position: relative; overflow: hidden; border: 1px solid #E5E9F2; border-radius: 10px; padding: 12px 14px; background: #FBFCFE; }
.kpi::after { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
.kpi-blue::after { background: #3668FC; }
.kpi-green::after { background: #16A34A; }
.kpi-orange::after { background: #F59E0B; }
.kpi-red::after { background: #E5484D; }
.kpi-label { display: block; font-size: 12px; color: #8A94AD; }
.kpi-val { display: block; font-size: 22px; font-weight: 700; color: #18233C; margin-top: 6px; }
.kpi-val.kpi-val-sm { font-size: 18px; }
.kpi-val small { font-size: 12px; color: #8A94AD; font-weight: 400; margin-left: 4px; }
.kpi-sub { display: block; font-size: 12px; color: #8A94AD; margin-top: 4px; }

/* 图表 */
.chart-duo { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.chart-duo .block-card { margin-bottom: 14px; }
.chart-card { border: 1px solid #E5E9F2; border-radius: 10px; padding: 12px 12px 4px; }
.chart-head { display: flex; align-items: baseline; gap: 8px; font-size: 13px; font-weight: 600; color: #18233C; }
.chart-head small { font-size: 12px; color: #8A94AD; font-weight: 400; }
.chart-box { width: 100%; height: 300px; }
.chart-box.sm { height: 260px; }

/* 工具栏 */
.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; flex-wrap: wrap; }
.toolbar-left { display: flex; align-items: center; gap: 8px; }
.toolbar-tip { font-size: 12px; color: #8A94AD; }

/* 标签 */
.tag { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 12px; }
.tag-gray { background: #EEF1F6; color: #8A94AD; }
.tag-blue { background: #EAF1FF; color: #3668FC; }
.tag-green { background: #E8F7EE; color: #16A34A; }
.tag-orange { background: #FEF3E2; color: #B45309; }
.tag-red { background: #FEEBEC; color: #E5484D; }
.chip { margin: 0 4px 2px 0; }
.acc { font-weight: 600; color: #18233C; }
.rank { display: inline-block; width: 20px; height: 20px; line-height: 20px; border-radius: 50%; background: #EEF1F6; color: #8A94AD; font-size: 12px; text-align: center; }
.money { font-weight: 600; color: #18233C; }
.money.none { color: #E5484D; }
.op { color: #3668FC; cursor: pointer; }
.deal-rate { color: #16A34A; font-weight: 600; }
.pager { margin-top: 12px; text-align: right; }

/* 回款进度条 */
.pbar { height: 8px; background: #EEF1F6; border-radius: 5px; overflow: hidden; display: flex; }
.pbar.lg { height: 10px; }
.pbar i { display: block; height: 100%; }
.pbar i.paid { background: #16A34A; }
.pbar i.unpaid { background: #F3B94C; }
.recover-box { border: 1px solid #E5E9F2; border-radius: 10px; padding: 12px 14px; }
.recover-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
.recover-title { font-size: 13px; font-weight: 600; color: #18233C; }
.mini { font-size: 12px; color: #8A94AD; }
.mini b { color: #18233C; }

/* 详情弹窗 */
.detail-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
.detail-acc { font-size: 15px; font-weight: 700; color: #18233C; }
.detail-sub { font-size: 12px; color: #8A94AD; }

/* 品牌色 */
::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover, ::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
::v-deep .el-table__row:hover > td { background: #F5F8FF !important; }

@media (max-width: 1200px) {
  .kpi-row, .kpi-row.six { grid-template-columns: repeat(2, 1fr); }
  .chart-duo { grid-template-columns: 1fr; }
}
</style>